<template>
    <!-- *********************************************
    *                   Contacts List                *
    ********************************************** -->
    <div v-if="hasPermission && listCount && parsedAndFilteredList.length && !showLoader" class="contacts-list-wrapper">
        <div
        class="contacts-group"
        v-for="group in contactsListToDisplay"
        :key="group.id">
            <divider-generic :title="group.id" :applyBorder="false"/>
            <contact-item
            v-for="contact in group.contacts"
            :key="contact.id"
            :phoneContactData="contact"
            :sendType="sendType"
            @contactSelected="emit('contactSelected', $event)"/>
        </div>
    </div>
    <!-- *********************************************
    *                    EmptyState                  *
    ********************************************** -->
    <empty-state-generic
    v-if="(!hasPermission || !parsedAndFilteredList.length) && !showLoader"
    :illustration="emptyStateObject.icon"
    :hasButton="!hasPermission"
    :title="emptyStateObject.title"
    :description="emptyStateObject.description"
    :buttonLabel="t('modals.contacts.emptyStateNoPermission.actionButton')"
    @clicked="givePermissionsHandler"/>

    <loader-generic v-if="showLoader" fullSize/>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import ContactItem from '@/components/generic/modals/side/contacts/ContactItem'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import LoaderGeneric from '@/components/generic/loaders/Generic'

/* Vue & Assets */
import { ref, computed, onMounted, watch } from 'vue'
import { contactsListMock } from '@/assets/scripts/contactsMock'
import { parseContactsList, reSortAndFilterContacts } from '@/assets/scripts/parsers/contacts'

/* Store */
import { mapActions } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['contactSelected', 'hasPermission'])

/* Props
@sendType {String} - send information type ['email'(default), 'sms']
@destinationValues {Array} - list of already selected contact values
@searchValue {String} - value being search on input
@showOnlyBAIContacts {Boolena} - prop to show only the BAI contacts
*/
const props = defineProps({
    sendType: { type: String, default: 'email' },
    destinationValues: Array,
    searchValue: String,
    showOnlyBAIContacts: Boolean
})

const { contacts } = mapActions(moduleMapper.SHARE_AND_SEND_INFO)

const hasPermission = ref(false)
const contactsRequest = ref(null)
const contactsList = ([])
const listCount = ref(null)
const parsedAndFilteredList = ref([])
const showLoader = ref(false)

const emptyStateObject = computed(() => {
    if (!hasPermission.value) {
        return {
            title: t('modals.contacts.emptyStateNoPermission.title'),
            description: t('modals.contacts.emptyStateNoPermission.description'),
            icon: 'emptyState/beneficiaries'
        }
    } else if (parsedAndFilteredList.value.length) {
        return {
            title: t('modals.contacts.emptyStateNoContacts.title'),
            description: t('modals.contacts.emptyStateNoContacts.description'),
            icon: 'emptyState/beneficiaries'
        }
    } else {
        return {
            title: t('modals.contacts.emptyStateNoResults.title'),
            description: t('modals.contacts.emptyStateNoResults.description'),
            icon: 'emptyState/search'
        }
    }
})

const givePermissionsHandler = () => {
    // Request permission to access contacts
    window.ContactsX.requestPermission((success) => {
        console.log('[REQUEST PERMISSIONS SUCCESS]', success)
        hasPermission.value = success.read
        emit('hasPermission', hasPermission.value)

        // Get contacts list
        getContactsList()
    }, (error) => {
        console.log('[REQUEST PERMISSIONS ERROR]', error)
    })
}

const getContactsList = () => {
    if (!hasPermission.value) return

    // Get contacts list
    window.ContactsX.find((success) => {
        console.log('[GET CONTACTS SUCCESS]', success)
        contactsList.value = success

        // Parse and filter contacts list
        getParsedAndFilteredContacts()
    }, (error) => {
        console.log('[GET CONTACTS ERROR]', error)
    }, {
        fields: {
            phoneNumbers: props.sendType === 'sms',
            emails: props.sendType === 'email',
            imageData: true
        }
    })
}

const getParsedAndFilteredContacts = () => {
    parsedAndFilteredList.value = parseContactsList(contactsList.value, props.destinationValues, props.sendType, props.searchValue)

    if (props.sendType === 'sms') getBaiContacts()

    if (listCount.value === null) {
        listCount.value = parsedAndFilteredList.value.length
    }
}

const getBaiContacts = async () => {
    showLoader.value = true

    const contactsPayload = {
        data: {
            filterNotEnrolledContacts: true,
            contacts: parsedAndFilteredList.value.reduce((acc, contact) => {
                    contact.contacts.forEach(element => {
                        element.phoneNumbers.forEach(el => {
                            acc.push({
                                name: element.firstName,
                                msisdn: el.value,
                                isEnrolled: false
                            })
                        })
                    })
                    return acc
                }, []
            )
        }
    }

    try {
        if (!contactsRequest.value) {
            const res = await contacts(contactsPayload)
            contactsRequest.value = res.data.data.contacts
        }
        handleContactsRequestResponse()
    } catch (error) {

    } finally {
        setTimeout(() => {
            showLoader.value = false
        }, 100)
    }
}

const handleContactsRequestResponse = () => {
    parsedAndFilteredList.value = parsedAndFilteredList.value.map(element => {
        return {
            ...element,
            contacts: element.contacts.map(ele => {
                let contactEnrolledInfo = null

                const parsedPhoneNumbers = ele.phoneNumbers.map(el => {
                    if (contactsRequest.value.find(contact => contact.msisdn === el.value)) {
                        contactEnrolledInfo = contactsRequest.value.find(contact => contact.msisdn === el.value)
                    }

                    return {
                        ...el,
                        enrolledInfo: contactsRequest.value.find(contact => contact.msisdn === el.value),
                        isEnrolled: !!contactsRequest.value.find(contact => contact.msisdn === el.value)
                    }
                })

                return {
                    ...ele,
                    name: contactEnrolledInfo ? contactEnrolledInfo.name : ele.name,
                    contactEnrolledInfo: contactEnrolledInfo,
                    phoneNumbers: parsedPhoneNumbers
                }
            })
        }
    })

    if (props.showOnlyBAIContacts) {
        parsedAndFilteredList.value = parsedAndFilteredList.value.reduce((acc, groups) => {
            const transformedGroup = {
                ...groups,
                contacts: groups.contacts.filter(element => element.contactEnrolledInfo)
            }

            if (transformedGroup.contacts.length) {
                transformedGroup.contacts = transformedGroup.contacts.map(contact => {
                    return {
                        ...contact,
                        phoneNumbers: contact.phoneNumbers.filter(el => el.isEnrolled)
                    }
                })

                acc.push(transformedGroup)
            }

            return acc
        }, [])
    }

    const sendTypeToGet = props.sendType === 'email' ? 'emails' : 'phoneNumbers'
    parsedAndFilteredList.value = reSortAndFilterContacts(parsedAndFilteredList.value, props.searchValue, sendTypeToGet)
}

const contactsListToDisplay = computed(() => {
    const sendTypeToGet = props.sendType === 'email' ? 'emails' : 'phoneNumbers'

    return reSortAndFilterContacts(parsedAndFilteredList.value, props.searchValue, sendTypeToGet)
})

onMounted(() => {
    // If using mocks set contactList as mock file and set permissions to true
    if ((process.env.VUE_APP_MOCKS === 'true' && !window.cordova) || !window.cordova) {
        contactsList.value = contactsListMock
        hasPermission.value = true
        emit('hasPermission', hasPermission.value)
        getParsedAndFilteredContacts()
    } else {
        // Check if has permission to access contacts
        window.ContactsX.hasPermission((success) => {
            console.log('[HAS PERMISSIONS SUCCESS]', success)
            hasPermission.value = success.read
            emit('hasPermission', hasPermission.value)

            // Get contacts list
            getContactsList()
        }, (error) => {
            console.log('[HAS PERMISSIONS ERROR]', error)
        })
    }
})
</script>

<style lang="scss" scoped>
.contacts-list-wrapper {
    width: 100%;
    height: auto;
    padding: 0;

    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    .contacts-group {
        display: flex;
        flex-direction: column;

        :deep(.divider-generic-wrapper) {
            margin-bottom: 8px;
            padding-left: 16px;
        }

    }
}

.empty-state-wrapper {
    height: 100%;
}
</style>
